class StoreAvailability {
  constructor() {
    this.initElements();
    this.loadData();
    this.bindEvents();
    this.init();
  }

  initElements() {
    this.availabilityBlock = document.querySelector('[data-store-availability-inline]');
    this.availabilityText = document.querySelector('[data-availability-text]');
    this.modal = document.querySelector('[data-store-availability-modal]');
    this.modalOverlay = document.querySelector('[data-modal-overlay]');
    this.modalClose = document.querySelector('[data-modal-close]');
    this.storeList = document.querySelector('[data-store-list]');
    
    if (!this.availabilityBlock) return;
    
    this.productForm = document.querySelector('form[action*="/cart/add"]');
    this.variantInput = document.querySelector('input[name="id"]');
  }

  loadData() {
    try {
      // Load variant inventory data
      const inventoryScript = document.querySelector('[data-variant-store-inventory]');
      this.variantInventory = inventoryScript ? JSON.parse(inventoryScript.textContent) : [];
      
      // Load store details
      const storeDetailsScript = document.querySelector('[data-store-details]');
      this.storeDetails = storeDetailsScript ? JSON.parse(storeDetailsScript.textContent) : { stores: [], materialThresholds: {} };
      
      // Get current variant from URL or form
      this.currentVariantId = this.getCurrentVariantId();
      
      // Get selected store from localStorage or announcement bar
      this.selectedStore = this.getSelectedStore();
      
      // Get block settings
      this.settings = this.getBlockSettings();
      
    } catch (error) {
      console.error('Error loading store availability data:', error);
    }
  }

  getBlockSettings() {
    // Default settings fallback
    const defaultSettings = {
      defaultStore: 'yorkdale_showroom',
      inStockText: 'Available at',
      outOfStockText: 'Out of stock at',
      lowInventoryText: 'Low inventory available at',
      onlineOnlyText: 'Unavailable in stores, available online',
      selectStoreText: 'Select a store',
      excludeWarehouse: true
    };
    
    // Try to get settings from HTML data attribute
    try {
      const settingsElement = document.querySelector('[data-store-availability-settings]');
      if (settingsElement) {
        const settings = JSON.parse(settingsElement.dataset.storeAvailabilitySettings);
        // Merge with defaults to ensure all properties exist
        return { ...defaultSettings, ...settings };
      }
    } catch (error) {
      console.error('Error parsing store availability settings:', error);
    }
    
    return defaultSettings;
  }

  getCurrentVariantId() {
    if (this.variantInput && this.variantInput.value) {
      return this.variantInput.value;
    }
    
    // Try to get from URL
    const urlParams = new URLSearchParams(window.location.search);
    const variant = urlParams.get('variant');
    if (variant) return variant;
    
    // Try to get from product JSON
    const productScript = document.querySelector('[data-product-json]');
    if (productScript) {
      const product = JSON.parse(productScript.textContent);
      return product.selected_or_first_available_variant?.id?.toString();
    }
    
    return null;
  }

  getSelectedStore() {
    // Try localStorage first
    const storedStore = localStorage.getItem('selectedStore');
    if (storedStore) return storedStore;
    
    // Try announcement bar or other store selector
    const announcementStore = document.querySelector('[data-selected-store]');
    if (announcementStore && announcementStore.dataset.storeId) {
      return announcementStore.dataset.storeId;
    }
    
    // Fall back to default from settings (with safety check)
    return (this.settings && this.settings.defaultStore) ? this.settings.defaultStore : 'yorkdale_showroom';
  }

  bindEvents() {
    if (!this.availabilityBlock) return;

    // Click to open drawer
    if (this.availabilityText) {
      this.availabilityText.addEventListener('click', (e) => {
        e.preventDefault();
        this.openDrawer();
      });
    }

    // Drawer close events
    if (this.modalClose) {
      this.modalClose.addEventListener('click', () => this.closeDrawer());
    }
    if (this.modalOverlay) {
      this.modalOverlay.addEventListener('click', () => this.closeDrawer());
    }

    // Variant change listener
    if (this.productForm) {
      this.productForm.addEventListener('change', (e) => {
        if (e.target.name === 'id') {
          this.currentVariantId = e.target.value;
          this.updateAvailability();
          this.updateDrawer();
        }
      });
    }

    // Listen for store selection updates
    document.addEventListener('storeSelected', (e) => {
      this.selectedStore = e.detail.storeId;
      localStorage.setItem('selectedStore', this.selectedStore);
      // Don't update availability here - only update on variant changes
    });

    // ESC key to close drawer
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal && this.modal.classList.contains('open')) {
        this.closeDrawer();
      }
    });

    // Announcement menu store selector click handler
    document.addEventListener('click', (e) => {
      const storeSelector = e.target.closest('[data-store-selector="true"]');
      if (storeSelector) {
        e.preventDefault();
        e.stopPropagation();
        this.openDrawer();
      }
    });
  }

  init() {
    if (!this.availabilityBlock) return;
    this.updateAvailability();
    this.renderModal();
    
    // Initialize announcement menu store name
    this.updateAnnouncementMenuStore(this.selectedStore);
  }

  updateAvailability() {
    if (!this.currentVariantId || !this.availabilityText) return;

    const variantData = this.getVariantInventoryData(this.currentVariantId);
    if (!variantData) {
      this.showError('Variant data not found');
      return;
    }

    // Get all store statuses to show comprehensive availability
    const storeStatuses = this.getAllStoreStatuses(variantData);
    
    // Find the best store to display (prioritize available stores)
    const displayStore = this.getBestStoreToDisplay(storeStatuses);
    
    if (displayStore) {
      const displayStoreInventory = this.getStoreInventory(variantData, displayStore.id);
      this.renderComprehensiveAvailability(displayStoreInventory, displayStore, variantData.material, storeStatuses);
    } else {
      this.renderComprehensiveAvailability(null, null, variantData.material, storeStatuses);
    }
  }

  getVariantInventoryData(variantId) {
    return this.variantInventory.find(v => v.variant_id === variantId);
  }

  getStoreInventory(variantData, storeId) {
    const storeData = variantData.inventory.find(s => s.store === storeId);
    return storeData ? storeData.inventory : 0;
  }

  getStoreInfo(storeId) {
    return this.storeDetails.stores.find(s => s.id === storeId);
  }

  getInventoryStatus(inventory, material, storeInfo, variantData = null) {
    if (inventory <= 0) return 'out_of_stock';
    
    // Only show "low_inventory" if a custom product threshold is explicitly set
    if (variantData && variantData.productThreshold !== null && variantData.productThreshold !== undefined) {
      if (inventory <= variantData.productThreshold) {
        return 'low_inventory';
      }
    }
    
    // Otherwise, just return "in_stock" - no default threshold logic
    return 'in_stock';
  }

  getAllStoreStatuses(variantData) {
    if (!variantData || !this.storeDetails.stores) return { outOfStock: [], lowInventory: [], inStock: [] };

    const outOfStock = [];
    const lowInventory = [];
    const inStock = [];

    // Filter out warehouses if setting is enabled
    const stores = this.storeDetails.stores.filter(store => {
      if (this.settings && this.settings.excludeWarehouse && store.type === 'warehouse') {
        return false;
      }
      return true;
    });

    stores.forEach(store => {
      const inventory = this.getStoreInventory(variantData, store.id);
      const status = this.getInventoryStatus(inventory, variantData.material, store, variantData);
      
      switch (status) {
        case 'out_of_stock':
          outOfStock.push(store);
          break;
        case 'low_inventory':
          lowInventory.push(store);
          break;
        case 'in_stock':
          inStock.push(store);
          break;
      }
    });

    return { outOfStock, lowInventory, inStock };
  }

  getBestStoreToDisplay(storeStatuses) {
    // Priority order: 1. In stock stores, 2. Low inventory stores, 3. Out of stock stores
    
    // First, try to find an in-stock store
    if (storeStatuses.inStock && storeStatuses.inStock.length > 0) {
      return storeStatuses.inStock[0];
    }
    
    // If no in-stock stores, try low inventory stores
    if (storeStatuses.lowInventory && storeStatuses.lowInventory.length > 0) {
      return storeStatuses.lowInventory[0];
    }
    
    // If no available stores, use the first out of stock store
    if (storeStatuses.outOfStock && storeStatuses.outOfStock.length > 0) {
      return storeStatuses.outOfStock[0];
    }
    
    // Fallback to first store if no store statuses available
    if (this.storeDetails && this.storeDetails.stores && this.storeDetails.stores.length > 0) {
      // Filter out warehouses if setting is enabled
      const stores = this.storeDetails.stores.filter(store => {
        if (this.settings && this.settings.excludeWarehouse && store.type === 'warehouse') {
          return false;
        }
        return true;
      });
      return stores.length > 0 ? stores[0] : null;
    }
    
    return null;
  }

  renderComprehensiveAvailability(selectedInventory, selectedStoreInfo, material, storeStatuses) {
    if (!this.availabilityText) return;

    // Get button text with fallback default
    const buttonText = (this.settings && this.settings.availabilityButtonText) ? this.settings.availabilityButtonText : 'See in-store availability';
    
    // Show single button regardless of inventory status
    const html = `<span class="store-availability-text availability-button" style="cursor: pointer; text-decoration: underline;">${buttonText}</span>`;

    this.availabilityText.innerHTML = html;
  }

  buildAvailabilityInfo(storeStatuses) {
    const parts = [];
    
    // Add out of stock stores
    if (storeStatuses.outOfStock.length > 0) {
      const storeNames = storeStatuses.outOfStock.map(store => store.name);
      if (storeNames.length === 1) {
        parts.push(`Out of stock at ${storeNames[0]}`);
      } else if (storeNames.length === 2) {
        parts.push(`Out of stock at ${storeNames.join(' and ')}`);
      } else {
        const lastStore = storeNames.pop();
        parts.push(`Out of stock at ${storeNames.join(', ')} and ${lastStore}`);
      }
    }

    // Add limited inventory stores
    if (storeStatuses.lowInventory.length > 0) {
      const storeNames = storeStatuses.lowInventory.map(store => store.name);
      if (storeNames.length === 1) {
        parts.push(`Limited availability at ${storeNames[0]}`);
      } else if (storeNames.length === 2) {
        parts.push(`Limited availability at ${storeNames.join(' and ')}`);
      } else {
        const lastStore = storeNames.pop();
        parts.push(`Limited availability at ${storeNames.join(', ')} and ${lastStore}`);
      }
    }

    return parts.join('<br>');
  }

  renderAvailabilityText(inventory, storeInfo, material, variantData = null) {
    const status = this.getInventoryStatus(inventory, material, storeInfo, variantData);
    const storeName = storeInfo.name;
    
    let text = '';
    let className = 'store-availability-text';
    
    // Get text with fallback defaults
    const inStockText = (this.settings && this.settings.inStockText) ? this.settings.inStockText : 'Available at';
    const lowInventoryText = (this.settings && this.settings.lowInventoryText) ? this.settings.lowInventoryText : 'Low inventory available at';
    const outOfStockText = (this.settings && this.settings.outOfStockText) ? this.settings.outOfStockText : 'Out of stock at';
    
    switch (status) {
      case 'in_stock':
        text = `${inStockText} ${storeName}`;
        className += ' in-stock';
        break;
      case 'low_inventory':
        text = `${lowInventoryText} ${storeName}`;
        className += ' low-inventory';
        break;
      case 'out_of_stock':
        text = `${outOfStockText} ${storeName}`;
        className += ' out-of-stock';
        break;
    }

    if (this.availabilityText) {
      this.availabilityText.innerHTML = `<span class="${className}" style="cursor: pointer; text-decoration: underline;">${text}</span>`;
    }
  }

  showSelectStore() {
    const buttonText = (this.settings && this.settings.availabilityButtonText) ? this.settings.availabilityButtonText : 'See in-store availability';
    if (this.availabilityText) {
      this.availabilityText.innerHTML = `<span class="store-availability-text availability-button" style="cursor: pointer; text-decoration: underline;">${buttonText}</span>`;
    }
  }

  showError(message) {
    if (this.availabilityText) {
      this.availabilityText.innerHTML = `<span class="store-availability-text error">${message}</span>`;
    }
  }

  renderModal() {
    if (!this.storeList || !this.currentVariantId) return;

    const variantData = this.getVariantInventoryData(this.currentVariantId);
    if (!variantData) return;

    const stores = this.storeDetails.stores.filter(store => {
      // Exclude warehouse if setting is enabled
      if (this.settings.excludeWarehouse && store.type === 'warehouse') {
        return false;
      }
      return true;
    });

    let html = '';
    
    // Sort stores to put the selected store at the top
    const sortedStores = this.sortStoresForModal(stores);

    sortedStores.forEach(store => {
      const inventory = this.getStoreInventory(variantData, store.id);
      const status = this.getInventoryStatus(inventory, variantData.material, store, variantData);
      const isSelected = store.id === this.selectedStore;

      let statusText = '';
      let statusClass = '';
      
      switch (status) {
        case 'in_stock':
          statusText = 'In stock';
          statusClass = 'in-stock';
          break;
        case 'low_inventory':
          statusText = 'Low inventory';
          statusClass = 'low-inventory';
          break;
        case 'out_of_stock':
          statusText = 'Out of stock';
          statusClass = 'out-of-stock';
          break;
      }

      html += `
        <div class="store-item ${isSelected ? 'selected' : ''}" data-store-id="${store.id}">
          <div class="store-info">
            <h4 class="store-name">${store.name}</h4>
            <p class="store-address">${store.address}</p>
            <p class="store-details">
              <span class="store-hours">${store.hours}</span>
              ${store.phone ? `<span class="store-phone">${store.phone}</span>` : ''}
            </p>
          </div>
          <div class="store-status ${statusClass}">
            <span class="status-text">${statusText}</span>
            ${!isSelected ? '<button class="set-store-btn" data-set-store>Set as my store</button>' : '<span class="selected-badge">Your store</span>'}
          </div>
        </div>
      `;
    });

    this.storeList.innerHTML = html;
    this.bindModalEvents();
  }

  sortStoresForModal(stores) {
    if (!this.selectedStore) {
      return stores; // Return original order if no store is selected
    }

    const selectedStore = stores.find(store => store.id === this.selectedStore);
    const otherStores = stores.filter(store => store.id !== this.selectedStore);

    // Put selected store first, then the rest in original order
    return selectedStore ? [selectedStore, ...otherStores] : stores;
  }

  bindModalEvents() {
    // Set store buttons
    const setStoreButtons = this.storeList.querySelectorAll('[data-set-store]');
    setStoreButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        const storeItem = button.closest('[data-store-id]');
        const storeId = storeItem.dataset.storeId;
        this.setSelectedStore(storeId);
      });
    });
  }

  setSelectedStore(storeId) {
    this.selectedStore = storeId;
    localStorage.setItem('selectedStore', storeId);
    
    // Update announcement menu store name
    this.updateAnnouncementMenuStore(storeId);
    
    // Dispatch event for other components
    document.dispatchEvent(new CustomEvent('storeSelected', {
      detail: { storeId: storeId }
    }));
    
    // Don't update availability text - only update modal
    this.renderModal();
  }

  updateAnnouncementMenuStore(storeId) {
    // Find the announcement menu store link (looking for various possible titles)
    const storeLinks = document.querySelectorAll('[data-link-title]');
    let storeLink = null;
    
    // Look for store-related links (case insensitive)
    for (const link of storeLinks) {
      const title = link.getAttribute('data-link-title').toLowerCase();
      if (title.includes('store') || title.includes('stores') || title.includes('location')) {
        storeLink = link;
        break;
      }
    }
    
    if (storeLink) {
      const spanElement = storeLink.querySelector('span');
      if (spanElement) {
        if (storeId) {
          // Find the store info
          const storeInfo = this.getStoreInfo(storeId);
          if (storeInfo) {
            // Update with store name and restore original link behavior
            spanElement.textContent = storeInfo.name;
            storeLink.style.pointerEvents = 'auto';
            storeLink.removeAttribute('data-store-selector');
          }
        } else {
          // No store selected - show "Select a store" and add click handler
          spanElement.textContent = 'Select a store';
          storeLink.style.pointerEvents = 'none'; // Disable original link
          storeLink.setAttribute('data-store-selector', 'true');
        }
      }
    }
  }

  updateDrawer() {
    if (this.modal && this.modal.classList.contains('open')) {
      this.renderModal();
    }
  }

  openDrawer() {
    if (!this.modal) return;
    this.renderModal();
    this.modal.classList.add('open');
    document.body.style.overflow = 'hidden';
    
    // Force reflow to ensure transition works
    this.modal.offsetHeight;
  }

  closeDrawer() {
    if (!this.modal) return;
    this.modal.classList.remove('open');
    document.body.style.overflow = '';
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new StoreAvailability();
});

// Re-initialize on Shopify theme editor updates
document.addEventListener('shopify:section:load', () => {
  new StoreAvailability();
});
